// src/pages/Accounting/Receipts/components/ViewDrawer/index.tsx

import { useEffect } from "react";
import type { Receipt } from "../../types";
import { PALETTE } from "../../constants";
import { DrawerHeader } from "./DrawerHeader";
import { ReceiptImage } from "./ReceiptImage";
import {
  TotalsSection,
  LineItemsSection,
  MatchedSection,
  MetaSection,
} from "./DrawerSections";
import { DrawerActions } from "./DrawerActions";

interface ViewDrawerProps {
  receipt: Receipt;
  onClose: () => void;
  onEdit: () => void;
  onUnmatch: () => void;
  onDelete: () => void;
  onOpenInBankTransactions: () => void;
}

export function ViewDrawer({
  receipt,
  onClose,
  onEdit,
  onUnmatch,
  onDelete,
  onOpenInBankTransactions,
}: ViewDrawerProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const isMatched = receipt.matchStatus === "matched";
  const isSuggestion =
    receipt.matchStatus === "needs-review" && !!receipt.matchedTransaction;
  const canUnmatch = !!receipt.matchedTransaction;

  const velaMessage = isMatched
    ? "extracted every field below and matched this receipt to a bank transaction."
    : isSuggestion
    ? "extracted every field below. Confirm the suggested match below."
    : "extracted every field below. Confirm the details.";

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(14, 26, 26, 0.28)",
          zIndex: 40,
        }}
      />
      <div
        role="dialog"
        aria-label={`Receipt from ${receipt.vendorName || "unknown vendor"}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(480px, 100vw)",
          background: "#FBFBFB",
          boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.08)",
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <DrawerHeader
          vendorName={receipt.vendorName}
          date={receipt.date}
          source={receipt.source}
          uploadedAt={receipt.uploadedAt}
          onClose={onClose}
        />

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 22px 70px",
          }}
        >
          <p
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontSize: 12.5,
              color: PALETTE.ink2,
              margin: "0 0 14px",
              fontWeight: 500,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: PALETTE.vela,
                color: "#fff",
                fontWeight: 700,
                fontSize: 9,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              V
            </span>
            <span>
              <strong style={{ color: PALETTE.ink, fontWeight: 600 }}>
                Vela
              </strong>{" "}
              {velaMessage}
            </span>
          </p>

          <ReceiptImage receipt={receipt} />
          <TotalsSection receipt={receipt} />
          <LineItemsSection receipt={receipt} />
          <MatchedSection
            matched={receipt.matchedTransaction}
            isSuggestion={isSuggestion}
            onOpenInBankTransactions={onOpenInBankTransactions}
          />
          <MetaSection receipt={receipt} />
        </div>

        <DrawerActions
          onEdit={onEdit}
          onUnmatch={onUnmatch}
          onDelete={onDelete}
          canUnmatch={canUnmatch}
        />
      </div>
    </>
  );
}